import { MsgHistoryClient } from '@core/clients/MsgHistoryClient';
import { forEach } from 'lodash';

const SEARCH_CONVERSATIONS_API_CONTENT_TO_RETRIEVE = {
  CAMPAIGN: 'campaign',
  MESSAGE_RECORDS: 'messageRecords',
  AGENT_PARTICIPANTS: 'agentParticipants',
  AGENT_PARTICIPANTS_LEAVE: 'agentParticipantsLeave',
  AGENT_PARTICIPANTS_ACTIVE: 'agentParticipantsActive',
  CONSUMER_PARTICIPANTS: 'consumerParticipants',
  TRANSFERS: 'transfers',
  INTERACTIONS: 'interactions',
  MESSAGE_SCORES: 'messageScores',
  MESSAGE_STATUSES: 'messageStatuses',
  CONVERSATION_SURVEYS: 'conversationSurveys',
  SUMMARY: 'summary',
  SDES: 'sdes',
  UNAUTH_SDES: 'unAuthSdes',
  MONITORING: 'monitoring',
  RESPONSE_TIME: 'responseTime',
  INTENTS: 'intents',
  DIALOGS: 'dialogs',
  PREVIOUSLY_SUBMITTED_AGENT_SURVEYS: 'previouslySubmittedAgentSurveys',
};

const SEARCH_CONVERSATIONS_CONTENT = [
  SEARCH_CONVERSATIONS_API_CONTENT_TO_RETRIEVE.MESSAGE_RECORDS,
  SEARCH_CONVERSATIONS_API_CONTENT_TO_RETRIEVE.AGENT_PARTICIPANTS,
  SEARCH_CONVERSATIONS_API_CONTENT_TO_RETRIEVE.CONSUMER_PARTICIPANTS,
  SEARCH_CONVERSATIONS_API_CONTENT_TO_RETRIEVE.MESSAGE_STATUSES,
  SEARCH_CONVERSATIONS_API_CONTENT_TO_RETRIEVE.DIALOGS,
  SEARCH_CONVERSATIONS_API_CONTENT_TO_RETRIEVE.SDES,
];

const SEARCH_CONVERSATION_PARTICIPANTS = [
  SEARCH_CONVERSATIONS_API_CONTENT_TO_RETRIEVE.AGENT_PARTICIPANTS,
  SEARCH_CONVERSATIONS_API_CONTENT_TO_RETRIEVE.CONSUMER_PARTICIPANTS,
];

const ORDERING = {
  ASC: 'asc',
  DESC: 'desc',
};

const CAPPING_SETTINGS = {
  ConversationSummaryEvent: { quantity: 1, order: ORDERING.DESC },
  CartStatusEvent: { quantity: 1, order: ORDERING.DESC },
  ServiceActivityEvent: { quantity: 1, order: ORDERING.DESC },
  CustomerInfoEvent: { quantity: 1, order: ORDERING.DESC },
  MarketingCampaignInfoEvent: { quantity: 1, order: ORDERING.DESC },
  PersonalInfoEvent: { quantity: 1, order: ORDERING.DESC },
  PurchaseEvent: { quantity: 10, order: ORDERING.DESC },
  ViewedProductEvent: { quantity: 50, order: ORDERING.DESC },
  VisitorErrorEvent: { quantity: 10, order: ORDERING.DESC },
  LeadEvent: { quantity: 10, order: ORDERING.DESC },
  SearchContentEvent: { quantity: 30, order: ORDERING.DESC },
};

export function parseSdeCappingSettings(settings) {
  const parsed = [];
  forEach(settings, (setting, sdeKey) => {
    let sdeSetting = setting.quantity ? `:${setting.quantity}` : '';
    sdeSetting += setting.order ? `:${setting.order}` : '';
    if (sdeSetting) {
      parsed.push(sdeKey + sdeSetting);
    }
  });

  return parsed.join(',');
}

//
export function transformConversationsHistoryRequest(settings) {
  const body = {
    conversationId: settings.conversationId,
    contentToRetrieve: SEARCH_CONVERSATIONS_CONTENT,
    cappingConfiguration: parseSdeCappingSettings(CAPPING_SETTINGS),
  };

  const params = {
    offset: settings.offset,
    limit: settings.limit,
    NC: 'true',
  };

  return {
    config: {
      params,
    },
    body,
    method: MsgHistoryClient.HTTP_METHODS.POST,
  };
}

export function transformConversationsHistoryResponse(response) {
  return response.data.conversationHistoryRecords[0] || {};
}


export function transformConversationParticipantsRequest(settings) {
  const body = {
    conversationId: settings.conversationId,
    contentToRetrieve: SEARCH_CONVERSATION_PARTICIPANTS,
  };

  const params = {
    offset: 0,
    limit: 1,
    NC: 'true',
  };

  return {
    config: {
      params,
    },
    body,
    method: MsgHistoryClient.HTTP_METHODS.POST,
  };
}

/**
 * @param response {Object}
 * @param reducer {Function} - custom function to transform participants array
 * @return {*[]}
 */
export function transformConversationParticipantsResponse(
  response,
  reducer = null,
) {
  const record = response.data.conversationHistoryRecords[0];
  const allParticipants = [...record.agentParticipants, ...record.consumerParticipants];
  if (reducer) {
    return reducer(allParticipants);
  }
  return allParticipants;
}

/**
 * Curried reducer to transform
 * an array of participants into a map by role
 * {
 *   CONSUMER: [ID],
 *   MANAGER: [ID, ID, ID...],
 *   ASSIGNED_AGENT: [ID, ID, ID...]
 * }
 * @param brandId {String}
 */
export function participantsReducer(brandId) {
  const allowedPermissions = ['ASSIGNED_AGENT', 'MANAGER'];
  /**
   * @param participantsArray {Array}
   */
  return participantsArray => participantsArray
    .reduce((acc, participant) => {
      if (!participant.agentId) {
        acc.CONSUMER = [participant.participantId];
      } else if (allowedPermissions.includes(participant.permission)) {
        const id = `${brandId}.${participant.agentId}`;
        acc[participant.permission] = acc[participant.permission] ?
          acc[participant.permission].concat(id)
          : [id];
      }
      return acc;
    }, {});
}
